/**
 * useGlobalSearch — V107.52.34
 *
 * Reactive, in-memory global search across 7 entity types:
 *   1. Customers          (useCustomers)
 *   2. Deals              (useDeals)
 *   3. Faults             (useFaults — includeArchived: true)
 *   4. Jobs               (useJobs — last 90 days + undated)
 *   5. Service Requests   (useServiceRequests — includeArchived: true)
 *   6. Customer Services  (useCustomerServices — includeInactive: true)
 *   7. Serial Numbers     (async, debounced, only when query looks like a serial)
 *
 * All upstream hooks subscribe to Firestore in real-time; this hook adds
 * NO new subscriptions. Filtering + ranking is pure JS and runs sub-100ms
 * over typical (~3-4k record) datasets.
 *
 * Returns grouped, ranked, capped results for the current query + filter.
 */

import { useEffect, useMemo, useState } from 'react';
import { useCustomers } from './useCustomers';
import { useDeals } from './useDeals';
import { useFaults } from './useFaults';
import { useJobs } from './useJobs';
import { useServiceRequests } from './useServiceRequests';
import { useCustomerServices } from './useCustomerServices';
import { useFeatureGate } from './useFeatureGate';
import { useAllContacts, type ContactSearchRecord } from './useAllContacts';
import {
  detectQueryShape,
  scorePhoneMatch,
  sumFieldScores,
} from '@/utils/search-ranking';
import {
  customerPhoneFields,
  scoreCustomerFields,
} from '@/utils/customer-search';
import { customerServiceSearchFields } from '@/utils/customer-service-search';
import {
  dealSearchFields,
  dealPhoneFields,
  faultSearchFields,
  jobSearchFields,
  jobPhoneFields,
  serviceRequestSearchFields,
} from '@/utils/entity-search-fields';
import { searchSerialNumbers } from '@/services/serial-number.service';
import { DealStage } from '@/types/theo-workflow.types';
import type { Customer } from '@/types/schema';
import type { Job } from '@/types/schema';
import type { CustomerService } from '@/types/schema';
import type { Deal } from '@/types/theo-workflow.types';
import type { Fault } from '@/types/fault.types';
import type { ServiceRequest } from '@/types/service-request.types';
import type { SerialNumberRecord } from '@/types/theo-workflow.types';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type SearchEntityType =
  | 'customer'
  | 'contact'
  | 'deal'
  | 'fault'
  | 'job'
  | 'service_request'
  | 'customer_service'
  | 'serial';

export type SearchFilter = 'all' | SearchEntityType;

export interface SearchResultItemBase {
  id: string;
  type: SearchEntityType;
  /** Total score after activity boosts and smart-query boosts. */
  score: number;
  /** ISO timestamp used as tie-breaker (newest first). */
  updatedAt: string;
  /** Where to navigate when the row is clicked. */
  navigate: string;
  /** True for entities that are "active" (open faults, non-completed deals, today's jobs). */
  isActive: boolean;
}

export interface CustomerResult extends SearchResultItemBase {
  type: 'customer';
  customer: Customer;
}
export interface ContactResult extends SearchResultItemBase {
  type: 'contact';
  contact: ContactSearchRecord;
  /** Parent customer name, resolved from the in-memory customer list. */
  customerName: string;
}
export interface DealResult extends SearchResultItemBase {
  type: 'deal';
  deal: Deal;
}
export interface FaultResult extends SearchResultItemBase {
  type: 'fault';
  fault: Fault;
}
export interface JobResult extends SearchResultItemBase {
  type: 'job';
  job: Job;
}
export interface ServiceRequestResult extends SearchResultItemBase {
  type: 'service_request';
  request: ServiceRequest;
}
export interface CustomerServiceResult extends SearchResultItemBase {
  type: 'customer_service';
  service: CustomerService;
}
export interface SerialResult extends SearchResultItemBase {
  type: 'serial';
  serial: SerialNumberRecord;
}

export type SearchResultItem =
  | CustomerResult
  | ContactResult
  | DealResult
  | FaultResult
  | JobResult
  | ServiceRequestResult
  | CustomerServiceResult
  | SerialResult;

export interface SearchResults {
  /** Active items (open faults, active deals, today's jobs) — ranked first. */
  active: SearchResultItem[];
  /** Grouped, ranked results per entity type (excludes the active section to avoid dupes). */
  groups: Record<SearchEntityType, SearchResultItem[]>;
  /** Per-type counts (TOTAL match counts, before per-group cap). Used for filter chip badges. */
  counts: Record<SearchEntityType, number>;
  /** Sum of all counts. */
  totalCount: number;
  /** True while any underlying hook is still loading (informational). */
  loading: boolean;
  /** True while the async serial lookup is running for the current query. */
  serialsLoading: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Max number of items shown per entity group in the combined ("all") view. */
const PER_GROUP_CAP = 5;
/**
 * Larger cap applied to the ONE entity type the user has explicitly filtered
 * to — the 5-per-group cap only makes sense when every type shares the modal.
 * Without this, filtering to "Customers" still showed just 5, so a 6th-ranked
 * match was unreachable despite the "filter to see more" hint.
 */
const FILTERED_GROUP_CAP = 50;
/** Max number of items shown in the "Active Now" hero section. */
const ACTIVE_CAP = 4;
/** Debounce window before firing the async serial-number Firestore lookup. */
const SERIAL_DEBOUNCE_MS = 250;

const ACTIVE_DEAL_BOOST = 3;
const OPEN_FAULT_BOOST = 5;
const TODAY_JOB_BOOST = 3;
const RECENT_JOB_BOOST = 1;
const ACTIVE_SERVICE_REQUEST_BOOST = 2;
const ACTIVE_SUBSCRIPTION_BOOST = 1;

const SMART_PHONE_BOOST = 5;
const SMART_EMAIL_BOOST = 5;
const SMART_ACCOUNT_NUMBER_BOOST = 10;

// Stages considered "active" for ranking purposes.
const TERMINAL_DEAL_STAGES: ReadonlySet<string> = new Set([
  DealStage.COMPLETED,
  DealStage.LOST,
]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function todayLocalISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysSince(iso: string | undefined | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (isNaN(t)) return null;
  return (Date.now() - t) / (1000 * 60 * 60 * 24);
}

/**
 * Coerce a timestamp value to an ISO string for safe sorting.
 * Some collections (Deal, CustomerService) store Firestore Timestamp objects
 * that haven't been sanitized client-side — `.localeCompare` would crash
 * on those. This handles both shapes plus null/undefined.
 */
function coerceISO(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  // Firestore Timestamp — has a toDate() method
  if (typeof value === 'object' && value !== null) {
    const v = value as { toDate?: () => Date; seconds?: number };
    if (typeof v.toDate === 'function') {
      try {
        return v.toDate().toISOString();
      } catch {
        return '';
      }
    }
    if (typeof v.seconds === 'number') {
      return new Date(v.seconds * 1000).toISOString();
    }
  }
  return '';
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useGlobalSearch(query: string, filter: SearchFilter = 'all'): SearchResults {
  // 1. Subscribe to all upstream data (already in memory via real-time hooks).
  const { customers, loading: customersLoading } = useCustomers();
  const { deals, loading: dealsLoading } = useDeals();
  // includeArchived so the search can find resolved/historical faults too.
  const { faults, loading: faultsLoading } = useFaults({ includeArchived: true });
  const { jobs, loading: jobsLoading } = useJobs();
  const { requests: serviceRequests, loading: requestsLoading } = useServiceRequests({
    includeArchived: true,
  });
  // Customer services are an MSP-pack surface and /home/services is wrapped in
  // <RequireFeature>. Without the pack, skip the listener so search never
  // advertises (or dead-ends on) a module the tenant doesn't have — an empty
  // list also drops the "Active Services" chip, which self-hides at count 0.
  const hasCustomerServices = useFeatureGate('customer-services');
  const { services: customerServices, loading: servicesLoading } = useCustomerServices({
    includeInactive: true,
    enabled: hasCustomerServices,
  });

  // Contacts live in per-customer sub-collections, so they're loaded lazily
  // (one collectionGroup read, session-cached) only once the user actually
  // types a query and contacts are in scope for the active filter.
  // Align the contacts minimum with every other entity type (which searches
  // from the first character). The load is a one-shot, session-cached
  // collectionGroup read, so triggering it a character earlier is cheap.
  const wantContacts =
    query.trim().length >= 1 && (filter === 'all' || filter === 'contact');
  const { contacts: allContacts, loading: contactsLoading } = useAllContacts(wantContacts);

  const loading =
    customersLoading ||
    dealsLoading ||
    faultsLoading ||
    jobsLoading ||
    requestsLoading ||
    servicesLoading;

  // 2. Async serial lookup (debounced, only when query LOOKS like a serial).
  const [serials, setSerials] = useState<SerialNumberRecord[]>([]);
  const [serialsLoading, setSerialsLoading] = useState(false);

  const queryShape = useMemo(() => detectQueryShape(query), [query]);

  useEffect(() => {
    // Fast-path bail when query is too short or doesn't look like a serial.
    if (!queryShape.isSerialShaped || queryShape.normalized.length < 4) {
      setSerials([]);
      setSerialsLoading(false);
      return;
    }
    let cancelled = false;
    setSerialsLoading(true);
    const handle = setTimeout(async () => {
      try {
        const results = await searchSerialNumbers(queryShape.normalized);
        if (!cancelled) {
          setSerials(results);
          setSerialsLoading(false);
        }
      } catch (err) {
        console.error('[GlobalSearch] serial lookup failed', err);
        if (!cancelled) {
          setSerials([]);
          setSerialsLoading(false);
        }
      }
    }, SERIAL_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [queryShape.isSerialShaped, queryShape.normalized]);

  // 3. Compute results. Memoised on query, filter, and all source data.
  const results = useMemo<SearchResults>(() => {
    // "async lookup in flight" — covers both the serial Firestore lookup and
    // the lazy contacts collectionGroup load. Drives the header spinner +
    // "Searching…" copy.
    const asyncLoading = serialsLoading || contactsLoading;
    const empty: SearchResults = {
      active: [],
      groups: emptyGroups(),
      counts: emptyCounts(),
      totalCount: 0,
      loading,
      serialsLoading: asyncLoading,
    };
    if (!queryShape.normalized) return empty;

    const today = todayLocalISO();
    const tomorrow = addDays(today, 1);
    const allItems: SearchResultItem[] = [];

    // -- Customers --
    if (filter === 'all' || filter === 'customer') {
      for (const c of customers) {
        if (!c.id) continue;
        // Skip merged-stub records — they redirect via mergedInto and would just
        // be noise in search results.
        if (c.mergedInto) continue;

        // Field set (names, company, ABN, every address incl. multi-address,
        // account numbers + external IDs) lives in customer-search.ts so the
        // picker modals search the exact same fields.
        let score = scoreCustomerFields(c, queryShape);

        // Phone matching — digit-aware on both sides. Attempt whenever the query
        // carries ≥4 digits (not only fully phone-shaped queries) so a typed
        // 4-6 digit tail recalls the customer, matching the picker path
        // (matchesCustomerShape). scorePhoneMatch canonicalises AU country codes,
        // so a stored "+61…" matches a locally-typed "04…".
        if (
          queryShape.digitsOnly.length >= 4 &&
          !queryShape.isAccountNumber &&
          !queryShape.isEmail
        ) {
          let phoneHit = 0;
          for (const p of customerPhoneFields(c)) {
            phoneHit += scorePhoneMatch(queryShape.digitsOnly, p);
          }
          score += phoneHit;
          if (phoneHit > 0 && queryShape.isPhone) score += SMART_PHONE_BOOST;
        }

        // Smart-query boosts
        if (queryShape.isEmail && c.email && c.email.toLowerCase().includes(queryShape.normalized)) {
          score += SMART_EMAIL_BOOST;
        }
        if (
          queryShape.isAccountNumber &&
          c.accountNumber &&
          c.accountNumber.toLowerCase() === queryShape.normalized
        ) {
          score += SMART_ACCOUNT_NUMBER_BOOST;
        }

        if (score <= 0) continue;
        allItems.push({
          type: 'customer',
          id: c.id,
          score,
          updatedAt: coerceISO(c.updatedAt) || coerceISO(c.createdAt),
          navigate: `/home/customers?openId=${encodeURIComponent(c.id)}`,
          isActive: false,
          customer: c,
        });
      }
    }

    // -- Contacts (site contacts + other contacts on customers) --
    if (filter === 'all' || filter === 'contact') {
      // Resolve parent customer names from the in-memory customer list.
      const customerNameById = new Map<string, string>();
      for (const c of customers) {
        if (!c.id) continue;
        customerNameById.set(
          c.id,
          c.name ||
            [c.firstName, c.lastName].filter(Boolean).join(' ').trim() ||
            c.companyName ||
            '',
        );
      }

      for (const ct of allContacts) {
        if (!ct.id) continue;
        const roleText = Array.isArray(ct.contactType) ? ct.contactType.join(' ') : '';
        const fields = [ct.name, ct.email, ct.role, roleText, ct.notes];
        let score = sumFieldScores(queryShape.normalized, fields);

        if (
          queryShape.digitsOnly.length >= 4 &&
          !queryShape.isAccountNumber &&
          !queryShape.isEmail
        ) {
          const phoneHit = scorePhoneMatch(queryShape.digitsOnly, ct.phone);
          score += phoneHit;
          if (phoneHit > 0 && queryShape.isPhone) score += SMART_PHONE_BOOST;
        }

        if (queryShape.isEmail && ct.email && ct.email.toLowerCase().includes(queryShape.normalized)) {
          score += SMART_EMAIL_BOOST;
        }

        if (score <= 0) continue;

        const customerName = customerNameById.get(ct.customerId) || '';
        allItems.push({
          type: 'contact',
          // Composite id — contact ids are only unique within a customer.
          id: `${ct.customerId}:${ct.id}`,
          score,
          updatedAt: coerceISO(ct.updatedAt) || coerceISO(ct.createdAt),
          navigate: `/home/customers?openId=${encodeURIComponent(ct.customerId)}`,
          isActive: false,
          contact: ct,
          customerName,
        });
      }
    }

    // -- Deals --
    if (filter === 'all' || filter === 'deal') {
      for (const d of deals) {
        if (!d.id) continue;
        // Shared field list (entity-search-fields.ts) so the deal list page and
        // global search stay in lockstep — this now includes jobAddress +
        // contactName (previously global-search-only gap).
        let score = sumFieldScores(queryShape.normalized, dealSearchFields(d));

        if (
          queryShape.digitsOnly.length >= 4 &&
          !queryShape.isAccountNumber &&
          !queryShape.isEmail
        ) {
          let phoneHit = 0;
          for (const p of dealPhoneFields(d)) phoneHit += scorePhoneMatch(queryShape.digitsOnly, p);
          score += phoneHit;
          if (phoneHit > 0 && queryShape.isPhone) score += SMART_PHONE_BOOST;
        }

        if (score <= 0) continue;

        const isActive = !TERMINAL_DEAL_STAGES.has(String(d.stage));
        if (isActive) score += ACTIVE_DEAL_BOOST;

        allItems.push({
          type: 'deal',
          id: d.id,
          score,
          // Deal createdAt/updatedAt are typed as Timestamp; coerce defensively
          // since useDeals() doesn't sanitize like useFaults() does.
          updatedAt: coerceISO(d.updatedAt) || coerceISO(d.createdAt),
          navigate: `/home/deals/${d.id}`,
          isActive,
          deal: d,
        });
      }
    }

    // -- Faults --
    if (filter === 'all' || filter === 'fault') {
      for (const f of faults) {
        if (!f.id) continue;
        // Shared field list — the fault pipeline page filter reads the same set.
        const score = sumFieldScores(queryShape.normalized, faultSearchFields(f));
        if (score <= 0) continue;

        const isActive = !f.archived;
        const finalScore = score + (isActive ? OPEN_FAULT_BOOST : 0);

        allItems.push({
          type: 'fault',
          id: f.id,
          score: finalScore,
          updatedAt: coerceISO(f.updatedAt) || coerceISO(f.createdAt),
          // Open the fault detail by deep-linking to the pipeline page with an openId
          navigate: `/home/faults?openId=${encodeURIComponent(f.id)}`,
          isActive,
          fault: f,
        });
      }
    }

    // -- Jobs --
    if (filter === 'all' || filter === 'job') {
      for (const j of jobs) {
        if (!j.id) continue;
        // Shared field list — the jobs list page filter reads the same set, so
        // techName / id / dealCategory / material serials (previously list-only)
        // and notes / criticalNotes / contacts (previously global-only) are now
        // searchable on BOTH surfaces.
        let score = sumFieldScores(queryShape.normalized, jobSearchFields(j));

        if (
          queryShape.digitsOnly.length >= 4 &&
          !queryShape.isAccountNumber &&
          !queryShape.isEmail
        ) {
          let phoneHit = 0;
          for (const p of jobPhoneFields(j)) phoneHit += scorePhoneMatch(queryShape.digitsOnly, p);
          score += phoneHit;
          if (phoneHit > 0 && queryShape.isPhone) score += SMART_PHONE_BOOST;
        }

        if (score <= 0) continue;

        // Activity boost — today/tomorrow first, last 7 days nudge
        let isActive = false;
        if (j.date && (j.date === today || j.date === tomorrow)) {
          score += TODAY_JOB_BOOST;
          isActive = true;
        } else {
          const ds = daysSince(j.completedAt || j.createdAt);
          if (ds !== null && ds <= 7) score += RECENT_JOB_BOOST;
        }

        allItems.push({
          type: 'job',
          id: j.id,
          score,
          updatedAt: coerceISO(j.completedAt) || coerceISO(j.createdAt),
          navigate: `/home/jobs?openId=${encodeURIComponent(j.id)}`,
          isActive,
          job: j,
        });
      }
    }

    // -- Service Requests --
    if (filter === 'all' || filter === 'service_request') {
      for (const r of serviceRequests) {
        if (!r.id) continue;
        // Shared field list — the service-requests list page reads the same set
        // (adds assignedToName, previously global-only).
        const score = sumFieldScores(queryShape.normalized, serviceRequestSearchFields(r));
        if (score <= 0) continue;

        const isActive = !r.archived;
        const finalScore = score + (isActive ? ACTIVE_SERVICE_REQUEST_BOOST : 0);

        allItems.push({
          type: 'service_request',
          id: r.id,
          score: finalScore,
          updatedAt: coerceISO(r.updatedAt) || coerceISO(r.createdAt),
          navigate: `/home/service-requests?openId=${encodeURIComponent(r.id)}`,
          isActive,
          request: r,
        });
      }
    }

    // -- Customer Services --
    if (filter === 'all' || filter === 'customer_service') {
      for (const s of customerServices) {
        if (!s.id) continue;
        // Full identifier set (AVC / NBN loc / NTD / kit / SIM / mobile /
        // AP+NVR+switch serials / porting numbers …) lives in
        // customer-service-search.ts.
        const score = sumFieldScores(queryShape.normalized, customerServiceSearchFields(s));
        if (score <= 0) continue;

        const isActive = s.status === 'Active';
        const finalScore = score + (isActive ? ACTIVE_SUBSCRIPTION_BOOST : 0);

        allItems.push({
          type: 'customer_service',
          id: s.id,
          score: finalScore,
          updatedAt: coerceISO(s.updatedAt) || coerceISO(s.createdAt),
          navigate: `/home/services?openId=${encodeURIComponent(s.id)}`,
          isActive,
          service: s,
        });
      }
    }

    // -- Serial Numbers (already filtered by serial-number.service) --
    if (filter === 'all' || filter === 'serial') {
      for (const sn of serials) {
        if (!sn.id) continue;
        const fields = [sn.serialNumber, sn.itemName, sn.customerName, sn.supplier];
        const score = sumFieldScores(queryShape.normalized, fields);
        if (score <= 0) continue;
        // Prefer linking to the parent deal, falling back to customer/job.
        let nav = '/home/provisioning';
        if (sn.dealId) nav = `/home/deals/${sn.dealId}`;
        else if (sn.customerId) nav = `/home/customers?openId=${encodeURIComponent(sn.customerId)}`;
        else if (sn.jobIds?.[0]) nav = `/home/jobs?openId=${encodeURIComponent(sn.jobIds[0])}`;
        allItems.push({
          type: 'serial',
          id: sn.id,
          score: score + 2, // small intrinsic boost — serials are usually a precise hit
          updatedAt: coerceISO(sn.capturedAt),
          navigate: nav,
          isActive: false,
          serial: sn,
        });
      }
    }

    // 4. Sort: highest score first, then most recent.
    allItems.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return (b.updatedAt || '').localeCompare(a.updatedAt || '');
    });

    // 5. Counts (BEFORE caps).
    const counts = emptyCounts();
    for (const item of allItems) counts[item.type]++;

    // 6. Active section — top items flagged as active, capped.
    const active = allItems.filter((i) => i.isActive).slice(0, ACTIVE_CAP);
    const activeIds = new Set(active.map((i) => `${i.type}:${i.id}`));

    // 7. Grouped sections — exclude items already in the active section, cap per group.
    const groups = emptyGroups();
    for (const item of allItems) {
      if (activeIds.has(`${item.type}:${item.id}`)) continue;
      const bucket = groups[item.type];
      // When the user has narrowed to one entity type, show a much larger slice
      // of THAT type so lower-ranked matches are actually reachable. Other types
      // (none, in a filtered view) keep the small combined-view cap.
      const cap =
        filter !== 'all' && filter === item.type ? FILTERED_GROUP_CAP : PER_GROUP_CAP;
      if (bucket.length < cap) bucket.push(item);
    }

    return {
      active,
      groups,
      counts,
      totalCount: allItems.length,
      loading,
      serialsLoading: asyncLoading,
    };
  }, [
    queryShape,
    filter,
    customers,
    allContacts,
    deals,
    faults,
    jobs,
    serviceRequests,
    customerServices,
    serials,
    loading,
    serialsLoading,
    contactsLoading,
  ]);

  return results;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function emptyGroups(): Record<SearchEntityType, SearchResultItem[]> {
  return {
    customer: [],
    contact: [],
    deal: [],
    fault: [],
    job: [],
    service_request: [],
    customer_service: [],
    serial: [],
  };
}

function emptyCounts(): Record<SearchEntityType, number> {
  return {
    customer: 0,
    contact: 0,
    deal: 0,
    fault: 0,
    job: 0,
    service_request: 0,
    customer_service: 0,
    serial: 0,
  };
}

function addDays(yyyymmdd: string, days: number): string {
  const [y, m, d] = yyyymmdd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}
